import { isDefined } from "@clipboard-health/util-ts";
import { Button } from "@mui/material";
import { ExtraWorkedTimeType } from "@src/appV2/ExtraWorkedTimeRequests/types";
import { showErrorToast, showSuccessToast } from "@src/appV2/lib/Notifications";
import { logEvent } from "@src/appV2/lib/analytics";
import { HCF_USER_EVENTS } from "src/constants/firebaseEvents";
import { ExtraWorkedBreakTimeConfig, FacilityExtraWorkedTimeConfig } from "@src/interface/index";
import { closeOpenNegotiationsForFacility } from "src/api/rateNegotiations";
import { RateNegotiationConfigEnum } from "src/components/RateNegotiationModal/interfaces";
import { UPDATE_PROFILE } from "@src/modules/session";
import {
  FacilityCategoryNote,
  fetchAllNotes,
  updateEarlyClockInEnabledInsideFacility,
  updateFacilityProfile,
  updateNotes,
} from "../../../api/facility";
import { getUser } from "../../../api/user";
import { FACILITY_ROLES } from "../../../constants/facility";
import { getHostedTimeFacility, putHostedTimeFacility } from "../api/hostedTimeFacility";
import { getFacilityNFCTagsRequests } from "../api/nfcTagRequests";
import { NFCTagRequests } from "../nfcTagRequest.types";
import { getRateNegotiationToggleSubtitle } from "./utils";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags";
import {
  DEFAULT_MANDATORY_BREAK_POLICY_NOTE,
  DEFAULT_REST_BREAK_POLICY_NOTE,
  NoteFieldIdentifiers,
} from "@src/appV2/FacilityRules/constants";
import { findSupportArticleUrlById } from "@src/appV2/HelpCenter/utils";
import { NfcTagRequestStatus } from "@clipboard-health/contract-backend-main";

export interface FacilitySetting {
  key: string;
  title: string | JSX.Element;
  subtitle?: string | JSX.Element;
  checked?: boolean;
  loading?: boolean;
  disabled?: boolean;
  hidden?: boolean;
  // FIXME: Automatically disabled during ESLint config update
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange?: any;
  noOfTags?: number;
  // FIXME: Automatically disabled during ESLint config update
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onClick?: any;
  defaultValue?: number;
}
export interface NFCTagRequestsDataSource {
  tagId: string;
  tagNumber: number;
  tagStatus: string;
  tagLocation: string;
  // FIXME: Automatically disabled during ESLint config update
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onDeleteAction?: any;
  isEditingDisable?: boolean;
  // FIXME: Automatically disabled during ESLint config update
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  OnFinishSetUp?: any;
  // FIXME: Automatically disabled during ESLint config update
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange?: any;
  // FIXME: Automatically disabled during ESLint config update
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSave?: any;
  isButtonDisable?: boolean;
}

const saveFacility = async ({ updateData, currentUserID }) => {
  await updateFacilityProfile({
    ...updateData,
    performedBy: currentUserID,
  });
};

const nonDeletedNFCTagsList = (nfcTagRequestsList) =>
  nfcTagRequestsList.filter((tagRequest) => tagRequest.tagStatus !== NfcTagRequestStatus.DELETED);

export const getUserAndWorkplaceSettings = async ({
  isLoadings,
  setLoadings,
  currentUserID,
  userId,
  userAccess,
  isAdmin,
  ldFlags,
  setWorkplaceSettings,
  setNFCRequestList,
  setHasExistingNfcTagRecord,
  extraWorkedTimeAutoDeclinationReasonModalState,
  mandatoryBreakPolicyDialogState,
  restBreakPolicyDialogState,
  mandatoryBreakDuration,
  supportArticlesData,
  dispatch,
}) => {
  setLoadings({
    ...isLoadings,
    getUser: true,
  });
  const user = await getUser(userId);
  const nfcTagRequestsObj = await getFacilityNFCTagsRequests(userId);
  const hostedTimeFacility = isAdmin ? await getHostedTimeFacility(userId) : undefined;
  const facilityNotes = await fetchAllNotes(userId, { includeConfig: true });

  // Dispatch Redux action to update session profile with fresh facility data.
  // Only for facility users — admin session profile must not be overwritten.
  if (dispatch && user.facility && !isAdmin) {
    dispatch({
      type: UPDATE_PROFILE,
      data: user.facility,
    });
  }

  buildDataSource({
    isLoadings,
    setLoadings,
    currentUserID,
    userId,
    userAccess,
    isAdmin,
    ldFlags,
    facility: user.facility,
    facilityNotes,
    setWorkplaceSettings,
    nfcTagRequestsObj,
    setNFCRequestList,
    setHasExistingNfcTagRecord,
    hostedTimeDeviceId: hostedTimeFacility?.deviceId ?? 0,
    extraWorkedTimeAutoDeclinationReasonModalState,
    mandatoryBreakPolicyDialogState,
    restBreakPolicyDialogState,
    mandatoryBreakDuration,
    supportArticlesData,
    dispatch,
  });
  setLoadings({
    ...isLoadings,
    getUser: false,
  });
};

const buildDataSource = ({
  isLoadings,
  setLoadings,
  userId,
  ldFlags,
  userAccess,
  isAdmin,
  facility,
  facilityNotes,
  setWorkplaceSettings,
  currentUserID,
  nfcTagRequestsObj,
  setNFCRequestList,
  setHasExistingNfcTagRecord,
  hostedTimeDeviceId,
  extraWorkedTimeAutoDeclinationReasonModalState,
  mandatoryBreakPolicyDialogState,
  restBreakPolicyDialogState,
  mandatoryBreakDuration,
  supportArticlesData,
  dispatch,
}) => {
  const dataSource: FacilitySetting[] = [];
  const nfcRequestsDataSource: NFCTagRequestsDataSource[] = [];

  const isFacilityOrCbhAdmin = userAccess.includes(FACILITY_ROLES.ADMIN) || isAdmin;

  // Helper function to refresh settings after updates
  const refreshSettings = () => {
    void getUserAndWorkplaceSettings({
      isLoadings,
      setLoadings,
      currentUserID,
      userAccess,
      isAdmin,
      userId,
      ldFlags,
      setWorkplaceSettings,
      setNFCRequestList,
      setHasExistingNfcTagRecord,
      extraWorkedTimeAutoDeclinationReasonModalState,
      mandatoryBreakPolicyDialogState,
      restBreakPolicyDialogState,
      mandatoryBreakDuration,
      supportArticlesData,
      dispatch,
    });
  };

  const nfcTagRequestsList = nfcTagRequestsObj?.nfcTagRequests || [];

  // Set flag indicating whether any NFC tag record exists (including deleted ones)
  // This is used to determine whether to call POST (create) or PUT (update) when requesting new tags
  setHasExistingNfcTagRecord(nfcTagRequestsList.length > 0);

  const configureHostedTimeDeviceId = () => {
    let selectedValue: number = hostedTimeDeviceId || 0;
    dataSource.push({
      title: "CBH <> HostedTime integration for Empire Care device ID",
      key: "hostedTimeDeviceId",
      disabled: !isAdmin,
      hidden: !isAdmin,
      defaultValue: selectedValue,
      onChange: (e) => {
        selectedValue = Number(e.target?.value || 0);
      },
      onClick: () => {
        handleHostedTimeDeviceId(selectedValue);
      },
      loading: isLoadings.updatingHostedTimeDeviceId,
    });
  };

  const toggleMapleIntegrationFlag = () => {
    dataSource.push({
      title: "Is this workplace integrated with Maple?",
      key: "mapleIntegrationEnabled",
      checked: facility.mapleIntegrationEnabled,
      disabled: !isAdmin,
      onChange: (checked) => {
        handleMapleIntegrationToggle(checked);
      },
      loading: isLoadings.mapleIntegrationEnabled,
    });
  };

  const controlRateNegotiationFeature = () => {
    const supportArticleUrl = findSupportArticleUrlById(
      supportArticlesData,
      "pay-rate-negotiations"
    );
    dataSource.push({
      title: "Allow professionals to propose rates on shifts",
      subtitle: getRateNegotiationToggleSubtitle(supportArticleUrl),
      key: "rateNegotiation",
      checked: facility.rateNegotiation === RateNegotiationConfigEnum.ON,
      disabled: !(userAccess.includes(FACILITY_ROLES.ADMIN) || isAdmin),
      onChange: (checked) => {
        void handleRateNegotiationStatusChange(checked);
      },
      loading: isLoadings.updatingRateNegotiation,
    });
  };

  const allowHCPToTakeBreak = () => {
    const isChecked = facility.requiresLunchBreak;

    const mandatoryBreakPolicyNote = facilityNotes.find(
      (note: FacilityCategoryNote) =>
        note.identifier === NoteFieldIdentifiers.MANDATORY_BREAK_POLICY
    );

    const categoryId = mandatoryBreakPolicyNote?.categoryId;
    const note = mandatoryBreakPolicyNote?.note ?? DEFAULT_MANDATORY_BREAK_POLICY_NOTE;

    dataSource.push({
      title: (
        <>
          Require professionals to take a break
          {isChecked && (
            <>
              <br />
              <Button
                size="small"
                variant="text"
                onClick={async () => {
                  const content = await mandatoryBreakPolicyDialogState.openModal(note);
                  if (isDefined(content)) {
                    void handleRequiresLunchBreak(true, categoryId, content, note);
                  }
                }}
                sx={{ paddingLeft: 0 }}
              >
                Edit mandatory break policy
              </Button>
            </>
          )}
        </>
      ),
      subtitle: `Our workplace requires professionals to take a ${mandatoryBreakDuration} minute unpaid break for shifts longer than 6 hours in duration.`,
      key: "hcfLunchBreak",
      checked: isChecked,
      disabled: !isFacilityOrCbhAdmin,
      onChange: async (checked) => {
        let content: string | undefined;

        if (checked) {
          content = await mandatoryBreakPolicyDialogState.openModal(note);
        }

        void handleRequiresLunchBreak(checked, categoryId, content, note);
      },
      loading: isLoadings.updatingRequiresLunchBreak,
    });
  };

  const autoDeclineWorkedBreakPayment = () => {
    const config = facility.extraWorkedTimeConfig?.[ExtraWorkedTimeType.BREAK];
    const isChecked = Boolean(config?.isAutoDeclinationEnabled);
    const hasDeclinationReason = config?.autoDeclinationReason;
    const reason = config?.autoDeclinationReason;

    if (facility.requiresLunchBreak) {
      const supportArticleUrl = findSupportArticleUrlById(
        supportArticlesData,
        "automatic-breaks-and-worked-break-payment-requests"
      );
      dataSource.push({
        title: (
          <>
            Automatically decline expiring break payment requests
            {isChecked && hasDeclinationReason && (
              <>
                <br />
                <Button
                  size="small"
                  variant="text"
                  onClick={async () => {
                    const content =
                      await extraWorkedTimeAutoDeclinationReasonModalState.openModal(reason);
                    if (isDefined(content)) {
                      handleExtraWorkedTimeAutoDeclinationChange({ content });
                    }
                  }}
                  sx={{ paddingLeft: 0 }}
                >
                  Edit declination reason
                </Button>
              </>
            )}
          </>
        ),
        subtitle: (
          <>
            When enabled, expiring break payment requests will be declined. When disabled, they will
            be approved.
            {supportArticleUrl && (
              <>
                {" "}
                <a href={supportArticleUrl} target="_blank">
                  Learn more
                </a>
              </>
            )}
          </>
        ),
        key: "extraWorkedTimeDeclinationReason",
        checked: isChecked,
        disabled: !(
          isAdmin ||
          [FACILITY_ROLES.ADMIN, FACILITY_ROLES.SHIFT_MANAGEMENT, FACILITY_ROLES.INVOICES].some(
            (role) => userAccess.includes(role)
          )
        ),
        onChange: async (checked: boolean) => {
          let content: string | undefined;

          if (checked) {
            content = await extraWorkedTimeAutoDeclinationReasonModalState.openModal(reason);
          }

          if (!checked || isDefined(content)) {
            handleExtraWorkedTimeAutoDeclinationChange({ content, isEnabled: checked });
          }
        },
        loading: isLoadings.updatingExtraWorkedTimeAutoDeclination,
      });
    }
  };

  const allowProvidingRestBreaks = () => {
    const isChecked = facility.providesRestBreaks;

    const restBreakPolicyNote = facilityNotes.find(
      (note: FacilityCategoryNote) => note.identifier === NoteFieldIdentifiers.REST_BREAK_POLICY
    );

    const categoryId = restBreakPolicyNote?.categoryId;
    const note = restBreakPolicyNote?.note ?? DEFAULT_REST_BREAK_POLICY_NOTE;

    dataSource.push({
      title: (
        <>
          Provide optional paid rest periods?
          {isChecked && (
            <>
              <br />
              <Button
                size="small"
                variant="text"
                onClick={async () => {
                  const content = await restBreakPolicyDialogState.openModal(note);
                  if (isDefined(content)) {
                    void handleProvidesRestBreaks(true, categoryId, content, note);
                  }
                }}
                sx={{ paddingLeft: 0 }}
              >
                Edit paid rest period policy
              </Button>
            </>
          )}
        </>
      ),
      subtitle: "When enabled, the rest period policy will be visible to professionals in the app.",
      key: "restBreak",
      checked: isChecked,
      disabled: !isFacilityOrCbhAdmin,
      onChange: async (checked) => {
        let content: string | undefined;

        if (checked) {
          content = await restBreakPolicyDialogState.openModal(note);
        }

        void handleProvidesRestBreaks(checked, categoryId, content, note);
      },
      loading: isLoadings.updatingRestBreaks,
    });
  };

  const addPreventEarlyClockInToggle = () => {
    dataSource.push({
      title: "Prevent professionals from clocking in early",
      subtitle:
        "We will not charge you for clocked time before a shift's posted start time. Only applies to shifts worked after making this change. Please note that this may affect punctuality.",
      key: "togglePreventEarlyClockIn",
      checked: !facility.isEarlyClockInEnabled,
      disabled: !isFacilityOrCbhAdmin,
      onChange: (preventEarlyClockIn: boolean) => {
        handlePreventEarlyClockInChange(preventEarlyClockIn);
      },
      loading: false,
    });
  };

  const addRestrictOverworkShiftInvitesToggle = () => {
    if (!ldFlags[CbhFeatureFlag.RESTRICT_OVERWORK_INVITES_TOGGLE]) {
      return;
    }

    dataSource.push({
      key: "restrictOverworkShiftInvites",
      title: "Restrict shift invites that would exceed 17 hours per day",
      subtitle:
        "When enabled, workplace users cannot send invites that would cause a professional to work more than 17 hours within a 24-hour period.",
      checked: facility.restrictOverworkShiftInvites ?? false,
      disabled: !isFacilityOrCbhAdmin,
      hidden: !isFacilityOrCbhAdmin,
      onChange: async () => {
        await handleRestrictOverworkToggle(!facility.restrictOverworkShiftInvites);
      },
      loading: isLoadings.restrictOverworkShiftInvites,
    });
  };

  if (ldFlags[CbhFeatureFlag.SHOW_RATE_NEGOTIATION_CONTROL_TOGGLE]) {
    controlRateNegotiationFeature();
  }

  if (
    ldFlags[CbhFeatureFlag.MAPLE_INTEGRATION]?.enableWebhook ||
    ldFlags[CbhFeatureFlag.MAPLE_INTEGRATION]?.enableApi
  ) {
    toggleMapleIntegrationFlag();
  }

  configureHostedTimeDeviceId();

  addPreventEarlyClockInToggle();
  addRestrictOverworkShiftInvitesToggle();
  allowHCPToTakeBreak();
  autoDeclineWorkedBreakPayment();
  nonDeletedNFCTagsList(nfcTagRequestsList)?.map((tagRequest: NFCTagRequests, index) =>
    nfcRequestsDataSource.push({
      tagId: tagRequest._id,
      tagNumber: index + 1,
      tagStatus: tagRequest.tagStatus,
      tagLocation: tagRequest.tagLocation || "",
      isEditingDisable: tagRequest.tagLocation !== "" && tagRequest.tagLocation !== undefined,
      isButtonDisable: false,
    })
  );

  if (facility.fullAddress?.state === "California") {
    allowProvidingRestBreaks();
  }

  setWorkplaceSettings(dataSource);
  setNFCRequestList(nfcRequestsDataSource);

  const handleRateNegotiationStatusChange = async (checked: boolean) => {
    if (!userId || !currentUserID) {
      return;
    }
    setLoadings({
      ...isLoadings,
      getUser: false,
      updatingRateNegotiation: true,
    });

    let countOfNegotiationsClosed = 0;
    const previousStatus = facility.rateNegotiation;

    if (!checked) {
      // Negotiations to be closed before updating DB flag.
      // Using `await`, so that if error occurs in this API,
      // the DB flag remains unchanged and user can try again
      countOfNegotiationsClosed = await closeOpenNegotiationsForFacility(userId);
      showSuccessToast(`Closed ${countOfNegotiationsClosed ?? 0} negotiations`);
    }

    const updateData = {
      facilityId: userId,
      rateNegotiation: checked ? RateNegotiationConfigEnum.ON : RateNegotiationConfigEnum.OFF,
    };

    try {
      await saveFacility({ updateData, currentUserID });

      // Track Segment event after successful update
      logEvent(HCF_USER_EVENTS.RATE_NEGOTIATION_TOGGLED, {
        facilityId: userId,
        previousStatus,
        status: updateData.rateNegotiation,
        source: "facility-settings",
        ...(updateData.rateNegotiation === RateNegotiationConfigEnum.OFF && {
          negotiationsClosed: countOfNegotiationsClosed,
        }),
      });
    } finally {
      setLoadings({
        ...isLoadings,
        getUser: false,
        updatingRateNegotiation: false,
      });
      refreshSettings();
    }
  };

  const handleRequiresLunchBreak = async (
    checked: boolean,
    categoryId?: string,
    note?: string,
    previousNote?: string
  ) => {
    if (!userId || !currentUserID || !facility?.verificationPreference) {
      return;
    }

    try {
      setLoadings({
        ...isLoadings,
        updatingRequiresLunchBreak: true,
      });

      // The API will respond with 400 if we try to set the same content for the note.
      if (categoryId && note && note !== previousNote) {
        await updateNotes(userId, { categoryId, note });
      }

      await saveFacility({
        updateData: {
          facilityId: userId,
          requiresLunchBreak: checked,
          requiresLunchBreakSince: checked ? new Date().toISOString() : null,
        },
        currentUserID,
      });

      showSuccessToast(`Updated mandatory break settings.`);
    } catch {
      showErrorToast("Couldn't update mandatory break settings.");
    } finally {
      setLoadings({
        ...isLoadings,
        updatingRequiresLunchBreak: false,
      });

      refreshSettings();
    }
  };

  const handleMapleIntegrationToggle = (checked: boolean) => {
    if (!userId || !currentUserID) {
      return;
    }
    if (!facility?.verificationPreference) {
      return;
    }
    setLoadings({
      ...isLoadings,
      mapleIntegrationEnabled: true,
    });
    const updateData = {
      facilityId: userId,
      mapleIntegrationEnabled: checked,
    };
    void saveFacility({
      updateData,
      currentUserID,
    }).finally(() => {
      setLoadings({
        ...isLoadings,
        mapleIntegrationEnabled: false,
      });
      refreshSettings();
    });
  };

  const handlePreventEarlyClockInChange = (preventEarlyClockIn: boolean) => {
    if (!userId || !currentUserID) {
      return;
    }
    setLoadings({
      ...isLoadings,
      getUser: false,
      updatingAllowEarlyClockIn: true,
    });

    /**
     * The UI shows "Prevent professionals from clocking in early?".
     * But the database column is "isEarlyClockInEnabled", so we invert it
     */
    const willEarlyClockInBeEnabled = !preventEarlyClockIn;
    const messageVerb = willEarlyClockInBeEnabled ? "enable" : "disable";

    updateEarlyClockInEnabledInsideFacility(userId, willEarlyClockInBeEnabled)
      .then(() => {
        setLoadings({
          ...isLoadings,
          getUser: false,
          updatingAllowEarlyClockIn: false,
        });
        refreshSettings();
      })
      .then(() => showSuccessToast(`Early clock-in ${messageVerb}d successfully.`))
      .catch(() => {
        showErrorToast(`Couldn't ${messageVerb} early clock-in.`);
      });
  };

  const handleRestrictOverworkToggle = async (newValue: boolean) => {
    if (!userId || !currentUserID) {
      return;
    }

    try {
      setLoadings({
        ...isLoadings,
        restrictOverworkShiftInvites: true,
      });

      await saveFacility({
        updateData: {
          facilityId: userId,
          restrictOverworkShiftInvites: newValue,
        },
        currentUserID,
      });

      showSuccessToast("Setting updated successfully");
    } catch {
      showErrorToast("Failed to update setting");
    } finally {
      setLoadings({
        ...isLoadings,
        restrictOverworkShiftInvites: false,
      });

      refreshSettings();
    }
  };

  const handleExtraWorkedTimeAutoDeclinationChange = (params: {
    content?: string;
    isEnabled?: boolean;
  }) => {
    if (!isDefined(userId) || !isDefined(currentUserID)) {
      return;
    }
    setLoadings({
      ...isLoadings,
      updatingExtraWorkedTimeAutoDeclination: true,
    });

    const breakConfig: ExtraWorkedBreakTimeConfig = {};
    if (isDefined(params.isEnabled)) {
      breakConfig.isAutoDeclinationEnabled = params.isEnabled;
    }
    if (isDefined(params.content)) {
      breakConfig.autoDeclinationReason = params.content;
    }
    const requestParams: {
      facilityId: string;
      extraWorkedTimeConfig: FacilityExtraWorkedTimeConfig;
    } = {
      facilityId: facility.userId,
      extraWorkedTimeConfig: {
        [ExtraWorkedTimeType.BREAK]: breakConfig,
      },
    };

    updateFacilityProfile(requestParams)
      .then(() => {
        refreshSettings();
        if (isDefined(params.isEnabled)) {
          showSuccessToast(
            `Extra worked time auto declination ${
              params.isEnabled ? "enabled" : "disabled"
            } successfully.`
          );
        } else {
          showSuccessToast("Extra worked time auto declination reason text updated successfully.");
        }
      })
      .catch(() => {
        setLoadings({
          ...isLoadings,
          updatingExtraWorkedTimeAutoDeclination: false,
        });

        if (isDefined(params.isEnabled)) {
          showErrorToast(
            `Couldn't ${
              params.isEnabled ? "enabled" : "disabled"
            } extra worked time auto declination.`
          );
        } else {
          showErrorToast("Couldn't update extra worked time auto declination reason text.");
        }
      });
  };

  const handleHostedTimeDeviceId = (deviceId: number) => {
    if (!userId || !currentUserID || !isAdmin || !facility?.name) {
      return;
    }
    setLoadings({
      ...isLoadings,
      updatingHostedTimeDeviceId: true,
    });
    void putHostedTimeFacility({
      userId,
      name: facility.name,
      deviceId,
    }).finally(() => {
      setLoadings({
        ...isLoadings,
        updatingHostedTimeDeviceId: false,
      });
      refreshSettings();
    });
  };

  const handleProvidesRestBreaks = async (
    checked: boolean,
    categoryId?: string,
    note?: string,
    previousNote?: string
  ) => {
    if (!userId || !currentUserID) {
      return;
    }

    try {
      setLoadings({
        ...isLoadings,
        updatingRestBreaks: true,
      });

      // The API will respond with 400 if we try to set the same content for the note.
      if (categoryId && note && note !== previousNote) {
        await updateNotes(userId, { categoryId, note });
      }

      await saveFacility({
        updateData: {
          facilityId: userId,
          providesRestBreaks: checked,
          providesRestBreaksSince: checked ? new Date().toISOString() : null,
        },
        currentUserID,
      });

      showSuccessToast(`Updated rest period settings.`);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log({ error });
      showErrorToast("Couldn't update rest period settings.");
    } finally {
      setLoadings({
        ...isLoadings,
        updatingRestBreaks: false,
      });

      refreshSettings();
    }
  };
};
